import { Trans, useLingui } from '@lingui/react/macro';

import config from '@/config';
import InputFile from '@/design_system/InputFile';
import Message from '@/design_system/Message';
import { PhotoCardGrid } from '@/design_system/PhotoCard';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import { useEntityLogo, useUpdateEntity, useUploadLogo } from '@/models/entity';

export const Logo = ({ entityId }: { entityId: string }) => {
  const { t } = useLingui();
  const { data: entityLogo } = useEntityLogo(entityId);
  const { mutateAsync: uploadLogo, error } = useUploadLogo(entityId);
  const { mutateAsync: updateEntity } = useUpdateEntity(entityId);

  const mediumId = entityLogo ? entityLogo.substring(0, 50) : '';

  // Adding a mediumId to the URL is only useful to make sure we don't use the cached previous URL when uploading another image
  const remoteLogoUrl = entityLogo
    ? `${config.apiUrl}/entities/${entityId}/logo?type=square&mediumId=${mediumId}`
    : undefined;

  return (
    <Title as="h3" size="300">
      <Stack gap="1rem">
        <Trans id="settings.entities.logo.title">Logo</Trans>

        {remoteLogoUrl ? (
          <PhotoCardGrid
            media={[{ id: mediumId, url: remoteLogoUrl }]}
            onDelete={() =>
              updateEntity({
                logo: null,
              })
            }
            size="xxlarge"
          />
        ) : (
          <InputFile
            ariaLabel={t({
              id: 'settings.entities.logo.upload',
              message: 'Upload logo',
            })}
            type="photo"
            allowsMultiple={false}
            onUpload={async (files) => {
              const file = files[0];

              if (!file) {
                return;
              }

              await uploadLogo({ file });
            }}
          />
        )}
      </Stack>

      {!!error && error.message === 'The file must be smaller than 2MB' && (
        <Message type="error">
          <Trans id="settings.entities.logo.errors.size">File size exceeds 2MB limit</Trans>
        </Message>
      )}
    </Title>
  );
};
